// SEO Configuration and Utilities for TesisKirala.com
// Türkçe SEO uyumlu yapılandırma

export const SITE_NAME = 'TesisKirala.com';
export const SITE_URL = process.env.REACT_APP_URL || window.location.origin;
export const SITE_DESCRIPTION = 'Türkiye\'nin en kolay spor tesisi kiralama platformu. Halı saha, spor salonu, voleybol sahası, basketbol sahası kiralama. Hemen rezervasyon yapın!';

// Ana anahtar kelimeler
export const PRIMARY_KEYWORDS = [
  'saha kirala',
  'spor salonu kirala',
  'voleybol sahası kirala',
  'basketbol sahası kirala',
  'halı saha kirala',
  'saha kiralama',
  'spor salonu kiralama',
  'voleybol sahası kiralama',
  'basketbol sahası kiralama',
  'halı saha kiralama',
  'tesis kirala',
  'tesis kiralama',
  'salon kirala',
  'salon kiralama'
];

// Tesis türü eşleştirmeleri (SEO uyumlu Türkçe)
export const FACILITY_TYPE_SEO = {
  hali_saha: {
    name: 'Halı Saha',
    keywords: ['halı saha kirala', 'halı saha kiralama', 'halı saha rezervasyon', 'futbol sahası kirala'],
    schemaType: 'SportsActivityLocation',
    sportType: 'Football'
  },
  kapali_spor_salonu: {
    name: 'Kapalı Spor Salonu',
    keywords: ['spor salonu kirala', 'spor salonu kiralama', 'kapalı spor salonu', 'salon kiralama'],
    schemaType: 'SportsActivityLocation',
    sportType: 'Sports'
  },
  voleybol_sahasi: {
    name: 'Voleybol Sahası',
    keywords: ['voleybol sahası kirala', 'voleybol sahası kiralama', 'voleybol salonu'],
    schemaType: 'SportsActivityLocation',
    sportType: 'Volleyball'
  },
  basketbol_sahasi: {
    name: 'Basketbol Sahası',
    keywords: ['basketbol sahası kirala', 'basketbol sahası kiralama', 'basketbol salonu'],
    schemaType: 'SportsActivityLocation',
    sportType: 'Basketball'
  },
  tenis_kortu: {
    name: 'Tenis Kortu',
    keywords: ['tenis kortu kirala', 'tenis kortu kiralama', 'tenis sahası'],
    schemaType: 'SportsActivityLocation',
    sportType: 'Tennis'
  },
  yuzme_havuzu: {
    name: 'Yüzme Havuzu',
    keywords: ['yüzme havuzu kirala', 'havuz kiralama'],
    schemaType: 'SportsActivityLocation',
    sportType: 'Swimming'
  }
};

// Şehir bazlı SEO verileri
export const CITY_SEO_DATA = {
  'İstanbul': {
    districts: ['Kadıköy', 'Beşiktaş', 'Üsküdar', 'Ataşehir', 'Maltepe', 'Kartal', 'Pendik', 'Bakırköy', 'Beylikdüzü', 'Sarıyer'],
    population: '15+ milyon',
    keywords: ['istanbul saha kirala', 'istanbul halı saha', 'istanbul spor salonu']
  },
  'Ankara': {
    districts: ['Çankaya', 'Keçiören', 'Mamak', 'Etimesgut', 'Yenimahalle'],
    population: '5+ milyon',
    keywords: ['ankara saha kirala', 'ankara halı saha', 'ankara spor salonu']
  },
  'İzmir': {
    districts: ['Karşıyaka', 'Bornova', 'Konak', 'Buca', 'Bayraklı'],
    population: '4+ milyon',
    keywords: ['izmir saha kirala', 'izmir halı saha', 'izmir spor salonu']
  }
};

// Meta description oluşturucu
export const generateMetaDescription = (facility) => {
  if (!facility) return SITE_DESCRIPTION;
  
  const facilityType = FACILITY_TYPE_SEO[facility.facility_type]?.name || 'Spor Tesisi';
  const location = `${facility.district}, ${facility.city}`;
  
  return `${facility.name} - ${location} ${facilityType.toLowerCase()} kiralama. ` +
    `Saatlik ${facility.price_per_hour}₺'den başlayan fiyatlarla hemen rezervasyon yapın. ` +
    `Online saha kiralama, kolay ödeme, anında onay.`;
};

// Meta title oluşturucu
export const generateMetaTitle = (facility) => {
  if (!facility) return `${SITE_NAME} - Spor Tesisi Kiralama Platformu`;
  
  const facilityType = FACILITY_TYPE_SEO[facility.facility_type]?.name || 'Spor Tesisi';
  return `${facility.name} | ${facility.district} ${facilityType} Kirala | ${SITE_NAME}`;
};

// Sayfa bazlı meta keywords oluşturucu
export const generateKeywords = (facility, city, district) => {
  const baseKeywords = [...PRIMARY_KEYWORDS];
  
  if (facility) {
    const typeKeywords = FACILITY_TYPE_SEO[facility.facility_type]?.keywords || [];
    baseKeywords.push(...typeKeywords);
    
    // Bölgesel anahtar kelimeler
    if (facility.city) {
      baseKeywords.push(`${facility.city.toLowerCase()} saha kirala`);
      baseKeywords.push(`${facility.city.toLowerCase()} ${FACILITY_TYPE_SEO[facility.facility_type]?.name.toLowerCase() || 'tesis'}`);
    }
    if (facility.district) {
      baseKeywords.push(`${facility.district.toLowerCase()} saha kirala`);
      baseKeywords.push(`${facility.district.toLowerCase()} halı saha`);
    }
  }
  
  if (city) {
    baseKeywords.push(`${city.toLowerCase()} saha kirala`);
    baseKeywords.push(`${city.toLowerCase()} spor salonu`);
  }
  
  if (district) {
    baseKeywords.push(`${district.toLowerCase()} saha kirala`);
  }
  
  return [...new Set(baseKeywords)].join(', ');
};

// JSON-LD Organization Schema
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.webp`,
  description: SITE_DESCRIPTION,
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'TR',
    addressLocality: 'İstanbul'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+90-212-123-45-67',
    contactType: 'customer service',
    availableLanguage: 'Turkish'
  },
  sameAs: [
    'https://www.facebook.com/tesiskirala',
    'https://www.instagram.com/tesiskirala',
    'https://twitter.com/tesiskirala'
  ]
});

// JSON-LD WebSite Schema (Arama kutusu için)
export const getWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESCRIPTION,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/tesisler?search={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
});

// JSON-LD LocalBusiness Schema (Tesis için)
export const getFacilitySchema = (facility) => {
  if (!facility) return null;
  
  const facilityType = FACILITY_TYPE_SEO[facility.facility_type];
  
  return {
    '@context': 'https://schema.org',
    '@type': ['SportsActivityLocation', 'LocalBusiness'],
    '@id': `${SITE_URL}/tesis/${facility.facility_id}`,
    name: facility.name,
    description: facility.description || generateMetaDescription(facility),
    image: facility.images?.[0] || `${SITE_URL}/logo.webp`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: facility.address,
      addressLocality: facility.district,
      addressRegion: facility.city,
      addressCountry: 'TR'
    },
    geo: facility.google_maps_link ? {
      '@type': 'GeoCoordinates',
      url: facility.google_maps_link
    } : undefined,
    telephone: facility.phone,
    priceRange: `${facility.price_per_hour}₺/saat`,
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '08:00',
      closes: '23:00'
    },
    aggregateRating: facility.average_rating ? {
      '@type': 'AggregateRating',
      ratingValue: facility.average_rating,
      reviewCount: facility.review_count || 0,
      bestRating: 5,
      worstRating: 1
    } : undefined,
    amenityFeature: facility.features?.map(feature => ({
      '@type': 'LocationFeatureSpecification',
      name: feature,
      value: true
    })),
    sport: facilityType?.sportType,
    url: `${SITE_URL}/tesis/${facility.facility_id}`,
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/tesis/${facility.facility_id}`,
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform'
        ]
      },
      result: {
        '@type': 'Reservation',
        name: 'Saha Rezervasyonu'
      }
    }
  };
};

// JSON-LD BreadcrumbList Schema
export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined
  }))
});

// JSON-LD FAQPage Schema
export const getFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Halı saha nasıl kiralanır?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'TesisKirala.com üzerinden istediğiniz halı sahayı seçin, müsait saatleri görüntüleyin ve online rezervasyon yapın. Ödemenizi tamamladıktan sonra rezervasyonunuz onaylanır.'
      }
    },
    {
      '@type': 'Question',
      name: 'Saha kiralama ücretleri ne kadar?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Saha kiralama ücretleri tesise, konuma ve saate göre değişmektedir. TesisKirala.com üzerinden tüm fiyatları karşılaştırabilir ve size en uygun tesisi seçebilirsiniz.'
      }
    },
    {
      '@type': 'Question',
      name: 'Rezervasyon iptali yapılabilir mi?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Rezervasyon iptal koşulları her tesis için farklılık gösterebilir. İptal politikası rezervasyon sırasında belirtilmektedir.'
      }
    }
  ]
});

// JSON-LD ItemList Schema (Tesis listesi için)
export const getFacilityListSchema = (facilities, city, district) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: district 
    ? `${district}, ${city} Spor Tesisleri` 
    : city 
      ? `${city} Spor Tesisleri`
      : 'Türkiye Spor Tesisleri',
  description: `${city || 'Türkiye'} ${district ? district + ' ' : ''}bölgesindeki kiralık spor tesisleri listesi`,
  numberOfItems: facilities.length,
  itemListElement: facilities.map((facility, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'SportsActivityLocation',
      name: facility.name,
      url: `${SITE_URL}/tesis/${facility.facility_id}`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: facility.district,
        addressRegion: facility.city
      },
      priceRange: `${facility.price_per_hour}₺/saat`
    }
  }))
});

// Canonical URL oluşturucu
export const getCanonicalUrl = (path) => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
};

// Hreflang tags (çoklu dil desteği için hazırlık)
export const getHreflangTags = (path) => [
  { rel: 'alternate', hreflang: 'tr', href: `${SITE_URL}${path}` },
  { rel: 'alternate', hreflang: 'x-default', href: `${SITE_URL}${path}` }
];

export default {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION,
  PRIMARY_KEYWORDS,
  FACILITY_TYPE_SEO,
  generateMetaDescription,
  generateMetaTitle,
  generateKeywords,
  getOrganizationSchema,
  getWebSiteSchema,
  getFacilitySchema,
  getBreadcrumbSchema,
  getFAQSchema,
  getFacilityListSchema,
  getCanonicalUrl
};
